/* eslint-disable max-lines */
import * as React from 'react';
import {
    MoreHorizontal,
    PlusCircle,
    EditIcon,
} from 'lucide-react';
import {
    MagnifyingGlassIcon, Cross1Icon
} from '@radix-ui/react-icons';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from './dialog';
import {
    Button
} from './button';
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from './card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
    DropdownMenuSeparator
} from './dropdown-menu';
import {
    Input
} from './input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from './table';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    TooltipProvider
} from './tooltip';
import {
    Checkbox
} from './checkbox';

// function NewStyledTable({
//     columns, data, rowActions = [],tableActions = [], cardTitle = null, search = true, multiSelect = false, actionCondition = () => true
// }) {
// ✅ fix (define a props type)
type Column<T> = {
  field: keyof T;
  headerName: string;
  renderCell?: (row: T) => React.ReactNode;
};

type Action = {
  name: string;
  onclick: (id: string | number | (string | number)[]) => void;
  isDelete?: boolean;
};

interface NewStyledTableProps<T> {
  columns: Column<T>[];
  data: T[];
  rowActions?: Action[];
  tableActions?: Action[];
  cardTitle?: string | null;
  search?: boolean;
  multiSelect?: boolean;
  actionCondition?: (row: T) => boolean;
}

// ✅ underline: <T extends { id: string | number }>
function NewStyledTable<T extends { id: string | number }>({
  columns, data, rowActions = [], tableActions = [], cardTitle = null,
  search = true, multiSelect = false, actionCondition = () => true,
}: NewStyledTableProps<T>) {
    const [searchValue, setSearchValue] = React.useState<string>('');
    const [filteredData, setFilteredData] = React.useState<T[]>(data);
    const [checkedRows, setCheckedRows] = React.useState<(string | number)[]>([]);
    const [areCheckboxesVisible, setAreCheckboxesVisible] = React.useState(false);
    const [deleteAction, setDeleteAction] = React.useState<any>({});
    const [addAction, setAddAction] = React.useState<any>({});

    React.useEffect(() => {
        if (searchValue === '') {
            setFilteredData(data);
        } else {
            setFilteredData(
                data.filter((row) => {
                    return columns.some((column) => {
                        return row[column.field]
                            ?.toString()
                            ?.toLowerCase()
                            ?.includes(searchValue.toLowerCase());
                    });
                })
            );
        }
    }, [searchValue, data, columns]);

    const handleSearchInputChanges = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
    };

    const handleSelectAllToggle = () => {
        if (checkedRows.length === filteredData.length) {
            setCheckedRows([]);
        } else {
            setCheckedRows(filteredData.map((row) => row.id));
        }

    };

    const handleSelectRowToggle = (id) => {
        if (checkedRows.includes(id)) {
            setCheckedRows(checkedRows.filter((rowId) => rowId !== id));
        } else {
            setCheckedRows([...checkedRows, id]);
        }
    };

    React.useEffect(()=>{
        if (tableActions.length > 0) {
            setDeleteAction(tableActions.find(action => action.name === 'Delete Selected') || {});
            setAddAction(tableActions.find(action => action.name === 'Add New') || {});
        }
    },[tableActions]);

    return (
        <Card className="w-full border border-slate-200 shadow-sm rounded-lg bg-white">
            <CardHeader className="px-6 pt-5 pb-4 border-b border-slate-100">
                {cardTitle && (
                    <CardTitle className="mb-4 text-base font-semibold text-slate-900 tracking-tight">
                        {cardTitle}
                    </CardTitle>
                )}
                <div className="flex items-center gap-3">
                    {search && <SearchInput value={searchValue} onChange={handleSearchInputChanges} />}
                    <div className="ml-auto flex items-center gap-2">
                        {multiSelect && (
                            <Button
                                size="sm"
                                variant="outline"
                                className="h-9 gap-1.5 text-sm font-medium"
                                onClick={() => setAreCheckboxesVisible(!areCheckboxesVisible)}
                            >
                                {areCheckboxesVisible ? (
                                    <>
                                        <Cross1Icon className="h-3.5 w-3.5" />
                                        <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">Cancel</span>
                                    </>
                                ) : (
                                    <>
                                        <EditIcon className="h-3.5 w-3.5" />
                                        <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">Edit All</span>
                                    </>
                                )}
                            </Button>
                        )}
                        {tableActions.length > 0 && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button size="sm" className="h-9 gap-1.5 text-sm font-medium" onClick={addAction.onclick}>
                                            <PlusCircle className="h-4 w-4" />
                                            <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">Add New</span>
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-slate-900 text-white text-xs">
                                        <p>Add a new record to this table</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardContent className="relative p-0">
                <div className="overflow-auto max-h-[480px]">
                    <Table className="relative">
                        <TableHeader className="sticky top-0 z-10 bg-slate-50/80 backdrop-blur supports-[backdrop-filter]:bg-slate-50/70">
                            <TableRow className="border-0 border-b border-solid border-slate-200 hover:bg-transparent">
                                {multiSelect && areCheckboxesVisible && (
                                    <TableHead className="w-12 px-6">
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Checkbox
                                                        id="selectall"
                                                        checked={checkedRows.length === filteredData.length}
                                                        onClick={handleSelectAllToggle}
                                                    />
                                                </TooltipTrigger>
                                                <TooltipContent className="bg-slate-900 text-white text-xs">
                                                    <p>Select all</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </TableHead>
                                )}
                                {columns.map((column, index) => (
                                    <TableHead
                                        key={index}
                                        className="px-6 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500"
                                    >
                                        {column.headerName}
                                    </TableHead>
                                ))}
                                {rowActions.length > 0 && (
                                    <TableHead className="w-12 px-4">
                                        <span className="sr-only">Actions</span>
                                    </TableHead>
                                )}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredData.length === 0 ? (
                                <TableRow className="hover:bg-transparent border-0">
                                    <TableCell
                                        colSpan={columns.length + (rowActions.length > 0 ? 1 : 0) + (multiSelect && areCheckboxesVisible ? 1 : 0)}
                                        className="text-center py-12 text-sm text-slate-500"
                                    >
                                        {searchValue ? 'No results match your search.' : 'No records to display.'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredData.map((row: any) => (
                                    <TableRow
                                        key={row.id}
                                        className="border-0 border-b border-solid border-slate-100 last:border-b-0 hover:bg-slate-50/60 transition-colors"
                                    >
                                        {multiSelect && areCheckboxesVisible && (
                                            <TableCell className="px-6 py-3.5">
                                                <Checkbox
                                                    id={row.id}
                                                    checked={checkedRows.includes(row.id)}
                                                    onClick={() => handleSelectRowToggle(row.id)}
                                                />
                                            </TableCell>
                                        )}
                                        {columns.map((column, colIndex) => (
                                            <TableCell
                                                key={colIndex}
                                                className="px-6 py-3.5 text-sm text-slate-700 align-middle"
                                            >
                                                {column.renderCell ? column.renderCell(row) : String(row[column.field])}
                                            </TableCell>
                                        ))}
                                        {rowActions.length > 0 && (
                                            <TableCell className="w-12 px-4 py-3.5 text-right">
                                                {actionCondition(row) && (
                                                    <DropdownMenu modal={false}>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button
                                                                aria-haspopup="true"
                                                                size="icon"
                                                                variant="ghost"
                                                                className="h-8 w-8 p-0 border-none bg-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                                                            >
                                                                <MoreHorizontal className="h-4 w-4" />
                                                                <span className="sr-only">Toggle menu</span>
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end" className="w-36">
                                                            <DropdownMenuLabel className="text-xs text-slate-500">Actions</DropdownMenuLabel>
                                                            {rowActions.map((action) => (
                                                                <div key={action.name}>
                                                                    {action.isDelete && <DropdownMenuSeparator />}
                                                                    <DropdownMenuItem
                                                                        className={action.isDelete ? 'text-red-600 focus:text-red-700 focus:bg-red-50' : ''}
                                                                        onClick={() => action.onclick(row.id)}
                                                                    >
                                                                        {action.name}
                                                                    </DropdownMenuItem>
                                                                </div>
                                                            ))}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
            {areCheckboxesVisible && (
                <CardFooter className="px-6 py-3 border-t border-slate-100 bg-slate-50/40">
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 mr-1">{checkedRows.length} selected</span>
                        <Button size="sm" variant="outline" className="h-8" onClick={() => setCheckedRows([])}>
                            Clear
                        </Button>
                        <Dialog>
                            <DialogTrigger asChild>
                                <Button size="sm" className="h-8" disabled={checkedRows.length === 0}>
                                    Delete
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Delete Selected</DialogTitle>
                                </DialogHeader>
                                <DialogDescription>
                                    Are you sure you want to delete the selected items?
                                </DialogDescription>
                                <DialogFooter>
                                    <DialogTrigger asChild>
                                        <Button size="sm" onClick={() => deleteAction.onclick(checkedRows)}>
                                            Delete
                                        </Button>
                                    </DialogTrigger>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                </CardFooter>
            )}
        </Card>
    );
}
interface SearchInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}
const SearchInput: React.FC<SearchInputProps> = ({
    value, onChange
}) => (
    <div className="relative w-full max-w-sm">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
            className="w-full pl-9 pr-3 h-9 text-sm rounded-md bg-white border border-slate-200 shadow-none placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-slate-300 dark:bg-gray-950"
            placeholder="Search table..."
            type="search"
            value={value}
            onChange={onChange}
        />
    </div>
);

export default NewStyledTable;
